<template>
  <Restricted :authority="['access_flyfood']">
    <Container fluid>
      <v-alert v-if="groups.length === 0" type="error" dense>
        You do not belong to a valid group, please reach out for support!
      </v-alert>
      <Row align="center" justify="center">
        <DataTableFlyfood title="Orders"
                    endpoint="/flyfood"
                    :headers.sync="headers"
                    unique-column="orderId"
                    :initial-options="options"
                    :item.sync="Order"
                    :items.sync="orders"
                    :actions="['create', 'edit', 'delete']"
                    custom-create-dialog-text="New Order"
                    create-button-text="Order"
                    :item-deletion-allowed="itemAllowModify"
                    :item-editing-allowed="itemAllowModify"

        >
          <template v-slot:item.datePickUp="{ item }">
            <DateFlyfood :value="item.datePickUp" />
          </template>
          <template v-slot:item.yeast="{ item }">
            <Icon v-if="item.yeast">mdi-check</Icon>
          </template>
          <template v-slot:item.nipagin="{ item }">
            <Icon v-if="item.nipagin">mdi-check</Icon>
          </template>
          <template v-slot:item.container="{ item }">
            <Chip :color="containerColor(item.container)">{{item.container}}</Chip>
          </template>
          <template v-slot:item.closure="{ item }">
            <Chip :color="closureColor(item.closure)">{{item.closure}}</Chip>
          </template>

          <template #create-form-title>
            VDRC Fly Food Ordering
          </template>

          <template #create-form-content>
            <Divider/>

            <Row>
              <Column><Select v-model="Order.product" :items="productTypes"
                              :rules="[rules.required]" label="Product"/></Column>
              <Column><Select v-model="Order.customerGroup" :items="groups"
                              :rules="[rules.required]" label="Institute / Department"/></Column>
            </Row>
            <Row>
              <Column>
                <Select
                    v-model="Order.container"
                    :items="containerNames"
                    :rules="[rules.required]"
                    label="Container"
                    @change="onContainerChange"
                />
              </Column>
              <Column><DatePickerFlyfood v-model="Order.datePickUp" :allowed-weekdays="[1]" :rules="[rules.required]"
                                   :allowed-dates="allowedDates"
                                   :label="`Pick Up From - Calender Week: ${isoWeek(Order.datePickUp)}`"/></Column>
            </Row>
            <Row>
              <Column>
                <Select
                    v-model="Order.closure"
                    :items="availableClosures"
                    label="Closure"
                    :disabled="!Order.container"
                    :rules="[rules.required]"
                    :hint="!Order.container ? 'Select a container first' : ''"
                    persistent-hint
                />
              </Column>
              <Column><TextField v-model="Order.numberOfBoxes" label="Number of boxes"
                              :rules="[rules.required, rules.integer, rules.numberOfBoxesRange]"/></Column>
            </Row>
            <Row>
              <Column>
                <Row>
                  <Column><Toggle v-model="Order.yeast"
                                  label="Yeast"/></Column>
                  <Column><Toggle v-model="Order.nipagin"
                                  label="Nipagin"/></Column>
                </Row>
              </Column>
              <Column>
              </Column>
            </Row>
            <Row>
              <TextAreaFlyfood v-model="Order.comment"
                         label="Comment"></TextAreaFlyfood>
            </Row>
          </template>

          <template #update-form-title>
            Update an existing order
          </template>
          <template #update-form-content>
            <Divider/>

            <Row>
              <Column><Select v-model="Order.product" :items="productTypes"
                              :rules="[rules.required]" label="Product"/></Column>
              <Column><Select v-model="Order.customerGroup" :items="groups"
                              :rules="[rules.required]" label="Institute / Department"/></Column>
            </Row>
            <Row>
              <Column>
                <Select
                    v-model="Order.container"
                    :items="containerNames"
                    label="Container"
                    :rules="[rules.required]"
                    @change="onContainerChange"
                />
              </Column>
              <Column><DatePickerFlyfood v-model="Order.datePickUp" :allowed-weekdays="[1]" :rules="[rules.required]"
                                   :allowed-dates="allowedDates"
                                   :label="`Pick Up From - Calender Week: ${isoWeek(Order.datePickUp)}`"/></Column>
            </Row>
            <Row>
              <Column>
                <Select
                    v-model="Order.closure"
                    :items="availableClosures"
                    label="Closure"
                    :disabled="!Order.container"
                    :hint="!Order.container ? 'Select a container first' : ''"
                    :rules="!Order.container ? [] : [rules.required]"
                    persistent-hint
                />
              </Column>
              <Column><TextField v-model="Order.numberOfBoxes" label="Number of boxes"
                                 :rules="[rules.required, rules.integer, rules.numberOfBoxesRange]"/></Column>
            </Row>
            <Row>
              <Column>
                <Row>
                  <Column><Toggle v-model="Order.yeast"
                                  label="Yeast"/></Column>
                  <Column><Toggle v-model="Order.nipagin"
                                  label="Nipagin"/></Column>
                </Row>
              </Column>
              <Column>
              </Column>
            </Row>
            <Row>
              <TextAreaFlyfood v-model="Order.comment"
                        label="Comment"></TextAreaFlyfood>
            </Row>
          </template>

        </DataTableFlyfood>
      </Row>
    </Container>
    <template v-slot:fallback>
      <Container fluid>
        <Row align="center" justify="center">
          <Column>
            <span>You do not have access, please contact us via our <a href="https://vbc.atlassian.net/servicedesk/customer/portal/7">ticket system</a> or per <a href="mailto:sim@vbcf.ac.at">mail</a></span>
          </Column>
        </Row>
      </Container>
    </template>
  </Restricted>
</template>

<script>
import DataTableFlyfood from "../utils/DataTable/DataTableFlyfood.vue";
import DatePickerFlyfood from "../utils/DatePicker/DatePickerFlyfood.vue";
import TextAreaFlyfood from "../utils/TextArea/TextAreaFlyfood.vue";
import DateFlyfood from "../utils/Date/DateFlyfood.vue";
import axios from "axios";

export default {
  name: "Orders",
  components: {
    DateFlyfood, TextAreaFlyfood, DatePickerFlyfood, DataTableFlyfood},
  data: () => ({
    Order: {
      product: '',
      customerGroup: '',
      container: '',
      datePickUp: null,
      closure: '',
      numberOfBoxes: 0,
      yeast: false,
      nipagin: false,
    },
    options: {
      sortBy: [
        'datePickUp'
      ],
      sortDesc: [
          'desc'
      ]
    },
    groups: [''],
    productTypes: ['VDRC standard food', 'Powerfood'],
    containers: [
      {
        name: 'Vials',
        closures: ['None', 'Cotton wool plugs'],
      },
      {
        name: 'Bottles',
        closures: ['None', 'Foam plugs'],
      },
    ],
    rules: {
      integer: value => {
        if (value === null || value === undefined || value === '') {
          return 'Only integers allowed! E.g.: 123'
        }
        if (typeof value === 'number') {
          return Number.isInteger(value) || 'Only integers allowed! E.g.: 123'
        }
        if (typeof value === 'string') {
          const trimmed = value.trim()
          return (/^[+]?\d+$/.test(trimmed)) || 'Only integers allowed! E.g.: 123'
        }
        return 'Only integers allowed! E.g.: 123'
      },
      required: value => !!value || "Required",
      numberOfBoxesRange: value => (value >= 1 && value <= 200) || "Number has to be between 1 and 200"
    },
    orders: [],
    headers: [
      { text: 'OrderID', value: 'orderId', align: ' d-none' },
      { text: 'Pick Up From', value: 'datePickUp' },
      { text: 'Product', value: 'product' },
      { text: 'Container', value: 'container' },
      { text: 'Closure', value: 'closure' },
      { text: 'Yeast', value: 'yeast' },
      { text: 'Nipagin', value: 'nipagin' },
      { text: 'Number of Boxes', value: 'numberOfBoxes' },
      { text: 'Customer Group', value: 'customerGroup' },
      { text: 'Customer Name', value: 'customerName' },
      { text: 'Comment', value: 'comment' },
      // { text: 'Actions', value: 'action', sortable: false, width: 1 } // Required for Update & Delete btn
    ]
  }),
  computed: {
    containerNames() {
      return this.containers.map(c => c.name);
    },
    availableClosures() {
      const sel = this.containers.find(c => c.name === this.Order.container);
      return sel ? sel.closures : [];
    },
  },
  methods: {
    onContainerChange() {
      if (!this.availableClosures.includes(this.Order.closure)) {
        this.Order.closure = null;
      }
    },
    containerColor(container) {
      if (container === "Vials") {
        return "#6edf42";
      } else {
        return "#5fa8ff";
      }
    },
    closureColor(closure) {
      if (closure === "None") {
        return "#ff776c";
      } else if(closure === "Cotton wool plugs") {
        return "#ffe03d";
      } else {
        return "#8de1ff";
      }
    },
    allowedDates(dateStr) {
      const date = new Date(dateStr + 'T00:00:00');
      date.setDate(date.getDate() - 7);
      return date > Date.now();
    },
    isoWeek(date) {
      if (!date) return '00';
      date = new Date(date);
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      // Set to nearest Thursday: current date + 4 - current day number (Sun=7)
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      return String(weekNo).padStart(2, '0');
    },
    itemAllowModify(item) {
      const date = new Date(item.datePickUp);
      date.setDate(date.getDate() - 7);
      return date > Date.now();
    },
    async fetchGroups() {
      const {data} = await axios({
        method: 'get',
        url: '/flyfood/groups',
      });
      this.groups = data;
    }
  },
  mounted() {
    this.fetchGroups();
  }
}
</script>